/**
 * SELECTOR DE MARCA — con qué cliente está trabajando el operador.
 *
 * DEENEX Activa se opera done-for-you: no entra el dueño del local, entra alguien del equipo y
 * trabaja la cuenta de un comercio. El módulo tenía la marca clavada en el código, así que sólo
 * existía un cliente posible: cargar el segundo le pisaba los datos al primero.
 *
 * SE ELIGE LA MARCA, Y LISTO (pedido de Alan del 11/10/2026: el botón decía «Comercio», la ventana «Elegí el
 * comercio», el campo «Marca», y adentro había identificadores agrupados bajo la marca). Todo dice «Marca» y cada
 * opción se ve por su nombre («HATSU»). Por dentro cada opción sigue siendo un comercio, su propio inquilino con su
 * base de clientes, sus campañas y su WhatsApp: si una marca llega a tener varios locales, cada uno aparece aparte
 * con su identificador al lado («HATSU (hatsu-palermo)»), y nunca se mezclan sus bases (a un cliente de Palermo no le
 * llega una promo del Centro).
 *
 * La clave del equipo no es fricción decorativa: sin ella, cualquiera con la URL podría abrir el
 * comercio de otro y ver su base o mandar desde su WhatsApp. Se recuerda en el navegador para no
 * volver a pedirla en cada cambio (y se borra al salir del modo operador).
 *
 * MODO PERSONA — quien entró con su mail (Accesos) ya está identificado y ya pasó por la lista: no se
 * le pide la clave ni que declare su mail, los comercios salen de GET /accesos/marcas con su sesión y
 * se entra con el canje (`entrarComoPersona`). Ofrecerle la clave dejaría abierta una puerta que no
 * mira la lista. Sin sesión de persona, todo sigue como antes.
 *
 * PERSONA DE UN CLIENTE (Accesos, rol «comercio», según el canje): la lista trae sólo sus comercios (el
 * backend no le manda el catálogo ni los de otros) y no puede crear uno nuevo: elegir entre los suyos
 * es todo lo que hace acá. Los ve por su nombre (la marca guardada del comercio), no por el identificador
 * («hatsu-palermo»), que queda sólo de respaldo: si no tiene nombre o dos se llaman igual. Al entrar, el
 * nombre queda como el del comercio actual: antes no se guardaba ninguno y el botón y el menú mostraban el
 * identificador.
 *
 * DÓNDE VA — en el menú lateral, arriba de «WhatsApp» (`variante="menu"`, o `"menuCompacto"` con el menú colapsado: sólo
 * el ícono), para cambiar de comercio desde cualquier pantalla (pedido de Alan del 11/10/2026). Y como chip arriba de
 * cada pantalla de WhatsApp y de Reactivación (`"chip"`), para saber de un vistazo sobre qué cliente se trabaja. La
 * ventana va en un portal a `document.body`: el menú tiene `backdrop-blur` y `overflow-hidden`, y un `fixed` adentro
 * quedaba medido y recortado contra el menú, no contra la pantalla.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Store, Loader2, LogOut, Check, ChevronDown } from "lucide-react";
import { activaApi, haySesionPersona, type Comercio, type MarcaReal } from "../api";
import { accesosApi } from "../../accesos/api";
import { useEsEquipoDeenex } from "../hooks/useEsEquipoDeenex";

type Lista = { fuenteCatalogo: "plataforma" | "local"; catalogo: MarcaReal[]; marcas: { marca: string; campanas: number; comercios: Comercio[] }[]; sueltos: Comercio[]; total: number };

const CLAVE_KEY = "deenex-activa:clave-operador";
/** El email de quien opera. No es un secreto: se recuerda aun después de «Salir». */
const OPERADOR_KEY = "deenex-activa:operador";
/** Mismo patrón que valida el backend: un operador que no pasa se ignora allá, así que acá ni se manda. */
const OPERADOR_RE = /^[^\s<>]{1,80}$/;
const NUEVO = "__nuevo__";

/** Los comercios de la lista que no vienen del catálogo de la plataforma: los de cada marca y los sueltos. */
const comerciosDe = (l: Lista | null): Comercio[] => (l ? [...l.marcas.flatMap((m) => m.comercios), ...l.sueltos] : []);

/**
 * Cómo se ve una opción: por el nombre de su marca. El identificador sólo de respaldo, si no tiene nombre o si otra
 * opción se llama igual (dos locales de la misma marca).
 */
function nombreDeComercio(c: { brandId: string; marca?: string }, todos: { brandId: string; marca?: string }[]): string {
  const nombre = (c.marca || "").trim();
  if (!nombre) return c.brandId;
  const repetido = todos.some((o) => o.brandId !== c.brandId && (o.marca || "").trim().toLowerCase() === nombre.toLowerCase());
  return repetido ? `${nombre} (${c.brandId})` : nombre;
}

/** «La Fábrica» → «la-fabrica»: el identificador que se le propone a una marca nueva (se puede cambiar). */
function identificadorDe(nombre: string): string {
  return nombre
    .normalize("NFD")
    .replace(/[^\x20-\x7e]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    // primero el tope y después las puntas: al revés, un nombre largo quedaba cortado justo después de un guion
    .slice(0, 40)
    .replace(/^-+|-+$/g, "");
}

/** El formato que exige el backend (`SLUG_COMERCIO_RE`): de 3 a 40, minúsculas, números y guiones, sin guion en las puntas. */
const IDENTIFICADOR_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

/** «chip»: el botón chico de arriba de cada pantalla. «menu» / «menuCompacto»: el del menú lateral, expandido o colapsado. */
export type VarianteSelector = "chip" | "menu" | "menuCompacto";

export default function SelectorMarca({ onCambio, variante = "chip" }: { onCambio?: () => void; variante?: VarianteSelector }) {
  const [abierto, setAbierto] = useState(false);
  const [clave, setClave] = useState("");
  const [lista, setLista] = useState<Lista | null>(null);
  const [elegido, setElegido] = useState("");
  const [nuevoId, setNuevoId] = useState("");
  // El identificador de una marca nueva se arma solo con el nombre, hasta que alguien lo escribe a mano.
  const [idTocado, setIdTocado] = useState(false);
  const [nuevaMarca, setNuevaMarca] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  // Quién opera: la auditoría del WhatsApp oficial registra este email en cada acción (confirmar
  // un envío, ajustar saldo). Sin él, todo queda firmado por «<marca>@operador.deenex».
  const [operador, setOperador] = useState(() => {
    try { return localStorage.getItem(OPERADOR_KEY) || ""; } catch { return ""; }
  });
  const operadorLimpio = operador.trim();
  const operadorInvalido = !!operadorLimpio && !OPERADOR_RE.test(operadorLimpio);
  const actual = activaApi.marcaActual();
  const nombreActual = activaApi.nombreActual();
  const esOperador = activaApi.esOperador();
  // Entró con su mail: sin clave ni mail declarado (ver MODO PERSONA arriba).
  const persona = haySesionPersona();
  // Una persona de un cliente no crea comercios ni es «operador» (ver PERSONA DE UN CLIENTE arriba).
  const equipo = useEsEquipoDeenex();

  /** Muestra la lista y deja elegido el comercio actual (o el primero del padrón). */
  const mostrar = (l: Lista) => {
    setLista(l);
    const todos = [...l.catalogo.map((m) => m.brandId), ...l.marcas.flatMap((m) => m.comercios.map((c) => c.brandId)), ...l.sueltos.map((c) => c.brandId)];
    setElegido(todos.includes(actual) ? actual : l.catalogo[0]?.brandId || todos[0] || (equipo ? NUEVO : ""));
  };

  const traer = async (c: string) => {
    if (!c.trim()) return;
    setCargando(true); setError("");
    try {
      mostrar(await activaApi.listarMarcas(c.trim()));
      try { localStorage.setItem(CLAVE_KEY, c.trim()); } catch { /* sin storage */ }
    } catch (e: any) {
      setError(e?.message || "No pudimos validar la clave");
      setLista(null);
      try { localStorage.removeItem(CLAVE_KEY); } catch { /* noop */ }
    } finally { setCargando(false); }
  };

  /** Modo persona: los comercios salen de /accesos/marcas con la sesión de la persona. */
  const traerPersona = async () => {
    setCargando(true); setError("");
    try {
      mostrar(await accesosApi.marcas());
    } catch (e: any) {
      setError(e?.message || "No pudimos traer las marcas");
      setLista(null);
    } finally { setCargando(false); }
  };

  // Al abrir: en modo persona se traen los comercios; si no, con la clave ya guardada se va directo
  // al desplegable.
  useEffect(() => {
    if (!abierto) { setError(""); setNuevoId(""); setNuevaMarca(""); setIdTocado(false); return; }
    if (persona) {
      if (!lista) void traerPersona();
      return;
    }
    let guardada = "";
    try { guardada = localStorage.getItem(CLAVE_KEY) || ""; } catch { /* noop */ }
    if (guardada && !lista) { setClave(guardada); void traer(guardada); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  // Todas las opciones (las de la plataforma y las de Activa), para saber si un nombre se repite.
  const opciones = [...(lista?.catalogo || []).map((m) => ({ brandId: m.brandId, marca: m.marca })), ...comerciosDe(lista)];
  const rotulo = (c: { brandId: string; marca?: string }) => `${nombreDeComercio(c, opciones)}${c.brandId === actual ? " (actual)" : ""}`;

  // Marca nueva: el identificador tiene que tener la forma que acepta el backend y no ser el de una marca de la lista.
  // «Crear» una con el identificador de otra entraba a ésa sin avisar y le pisaba el nombre, que es el que firma sus
  // WhatsApp: escribir «Hatsú» entraba al HATSU real y lo renombraba.
  const idNuevo = nuevoId.trim().toLowerCase();
  const idYaUsado = elegido === NUEVO ? opciones.find((o) => o.brandId === idNuevo) : undefined;
  const avisoId = elegido !== NUEVO ? ""
    : idYaUsado ? `Ya hay una marca con ese identificador («${nombreDeComercio(idYaUsado, opciones)}»): elegila en la lista o cambiá el identificador.`
    : idNuevo && !IDENTIFICADOR_RE.test(idNuevo) ? "El identificador va de 3 a 40 caracteres: minúsculas, números y guiones, sin guion al principio ni al final."
    : !idNuevo && nuevaMarca.trim() ? "De ese nombre no sale un identificador: escribilo a mano (minúsculas, números y guiones)."
    : "";
  const puedeEntrar = !cargando && (elegido === NUEVO ? !!idNuevo && !avisoId : !!elegido);

  const entrar = async () => {
    if (!puedeEntrar) return;
    const esNuevo = elegido === NUEVO;
    const comercio = esNuevo ? idNuevo : elegido.trim().toLowerCase();
    setCargando(true); setError("");
    try {
      // al crear se declara la marca; al entrar a uno existente no se manda (no pisa la suya)
      const marca = esNuevo ? nuevaMarca.trim() : undefined;
      // El nombre con el que se va a ver en el menú: el de la opción elegida (con su identificador si otra se llama
      // igual), para el equipo y para un cliente. Antes el equipo lo tomaba sólo del catálogo de la plataforma, y al
      // elegir «HATSU» el menú pasaba a decir «hatsu». Sin nombre conocido no se manda: queda el que devuelve el canje.
      const elegida = opciones.find((o) => o.brandId === comercio);
      const nombreLegible = esNuevo
        ? nuevaMarca.trim() || idNuevo
        : elegida && (elegida.marca || "").trim() ? nombreDeComercio(elegida, opciones) : undefined;
      if (persona) {
        // El canje: quién opera sale de la sesión (el mail verificado), no de un campo.
        await activaApi.entrarComoPersona(comercio, marca, nombreLegible);
      } else {
        // Un email mal escrito no traba la entrada: se recuerda lo que haya, pero sólo viaja si pasa.
        try {
          if (operadorLimpio) localStorage.setItem(OPERADOR_KEY, operadorLimpio);
          else localStorage.removeItem(OPERADOR_KEY);
        } catch { /* sin storage */ }
        await activaApi.operadorLogin(
          clave.trim(), comercio, marca, nombreLegible,
          operadorLimpio && !operadorInvalido ? operadorLimpio : undefined,
        );
      }
      // recarga completa: el módulo lee el comercio al montar (borradores, listados, settings),
      // así que reutilizar la pantalla dejaría datos del comercio anterior a la vista.
      onCambio ? onCambio() : window.location.reload();
    } catch (e: any) {
      setError(e?.message || "No se pudo entrar a esa marca");
      setCargando(false);
    }
  };

  const salir = () => {
    activaApi.salirOperador();
    try { localStorage.removeItem(CLAVE_KEY); } catch { /* noop */ }
    window.location.reload();
  };

  if (!abierto) {
    if (variante === "menuCompacto") {
      return (
        <button
          onClick={() => setAbierto(true)}
          title={`Marca: ${nombreActual} · cambiar`}
          aria-label={`Marca: ${nombreActual}. Cambiar de marca`}
          className="w-11 h-10 rounded-xl flex items-center justify-center border border-slate-200 bg-white text-violet-600 hover:border-violet-300 hover:bg-violet-50 transition-colors"
        >
          <Store size={18} />
        </button>
      );
    }
    if (variante === "menu") {
      return (
        <button
          onClick={() => setAbierto(true)}
          title="Cambiar de marca"
          className="w-full flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left hover:border-violet-300 hover:bg-violet-50/40 transition-colors"
        >
          <Store size={16} className="text-violet-600 flex-shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">Marca</span>
            <span className="block truncate text-sm font-semibold text-slate-800">{nombreActual}</span>
          </span>
          <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
        </button>
      );
    }
    return (
      <button
        onClick={() => setAbierto(true)}
        title="Cambiar de marca"
        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 transition-colors"
      >
        <Store size={13} className="flex-shrink-0" />
        <span className="truncate max-w-[170px]">{nombreActual}</span>
        {esOperador && equipo && <span className="text-[9px] font-bold uppercase tracking-wider text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">operador</span>}
        <ChevronDown size={12} className="text-slate-400 flex-shrink-0" />
      </button>
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 backdrop-blur-sm p-4 pt-[10vh]" onClick={() => setAbierto(false)}>
      <div className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-4">
          <span className="w-9 h-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0"><Store size={17} /></span>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900">Elegí la marca</h2>
            <p className="text-xs text-slate-500 truncate">Trabajando ahora sobre <strong className="text-slate-700">{nombreActual}</strong></p>
          </div>
        </div>

        {!lista && persona ? (
          <div className="py-3 text-center">
            {error ? (
              <button onClick={() => void traerPersona()} className="text-sm font-semibold text-violet-700 hover:underline">
                Reintentar
              </button>
            ) : (
              <p className="inline-flex items-center gap-2 text-sm text-slate-500">
                <Loader2 size={14} className="animate-spin" /> Cargando las marcas…
              </p>
            )}
          </div>
        ) : !lista ? (
          <div>
            <label className="text-xs font-semibold text-slate-600">Clave del equipo</label>
            <div className="flex gap-2 mt-1.5">
              <input
                type="password" value={clave} autoFocus
                onChange={(e) => setClave(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && traer(clave)}
                placeholder="••••••••"
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <button onClick={() => traer(clave)} disabled={cargando || !clave.trim()}
                className="rounded-xl bg-violet-600 text-white px-4 text-sm font-semibold hover:bg-violet-700 disabled:opacity-40 inline-flex items-center gap-1.5">
                {cargando && <Loader2 size={14} className="animate-spin" />} Entrar
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Sin la clave, el módulo trabaja sobre la marca de demostración.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label htmlFor="selector-marca" className="text-xs font-semibold text-slate-600">Marca</label>
              <div className="relative mt-1.5">
                <select
                  id="selector-marca"
                  value={elegido}
                  onChange={(e) => setElegido(e.target.value)}
                  className="w-full appearance-none rounded-xl border border-slate-200 bg-white pl-3 pr-9 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-500 cursor-pointer"
                >
                  {/* Una opción por marca, por su nombre: primero las de la plataforma (si está configurada), después las de Activa. */}
                  {lista.catalogo.map((m) => <option key={m.brandId} value={m.brandId}>{rotulo(m)}</option>)}
                  {comerciosDe(lista).map((c) => <option key={c.brandId} value={c.brandId}>{rotulo(c)}</option>)}
                  {equipo && <option value={NUEVO}>➕ Marca nueva…</option>}
                </select>
                <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
              {lista.total === 0 && !lista.catalogo.length && (
                <p className="text-[11px] text-slate-500 mt-1.5">
                  {equipo ? "Todavía no hay marcas: creá la primera." : "No tenés marcas asignadas: pedíselo a tu contacto de Deenex."}
                </p>
              )}
            </div>

            {elegido === NUEVO && equipo && (
              <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-3 space-y-2.5">
                <div>
                  <label htmlFor="marca-nueva-nombre" className="text-xs font-semibold text-slate-600">Nombre de la marca</label>
                  <input
                    id="marca-nueva-nombre"
                    value={nuevaMarca} autoFocus
                    onChange={(e) => {
                      setNuevaMarca(e.target.value);
                      if (!idTocado) setNuevoId(identificadorDe(e.target.value));
                    }}
                    placeholder="HATSU"
                    className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                <div>
                  <label htmlFor="marca-nueva-id" className="text-xs font-semibold text-slate-600">Identificador</label>
                  <input
                    id="marca-nueva-id"
                    value={nuevoId}
                    onChange={(e) => { setNuevoId(e.target.value); setIdTocado(true); }}
                    onKeyDown={(e) => e.key === "Enter" && entrar()}
                    placeholder="hatsu"
                    aria-invalid={!!avisoId}
                    className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                  {avisoId
                    ? <p className="text-[11px] text-amber-700 mt-1">{avisoId}</p>
                    : <p className="text-[11px] text-slate-500 mt-1">Sale solo del nombre. Minúsculas, números y guiones; no se puede cambiar después.</p>}
                </div>
              </div>
            )}

            {/* En modo persona quién opera sale de la sesión: no se declara. */}
            {!persona && (
              <div>
                <label htmlFor="selector-operador" className="text-xs font-semibold text-slate-600">Tu email (queda registrado en lo que hagas)</label>
                <input
                  id="selector-operador"
                  type="email" autoComplete="email"
                  value={operador}
                  onChange={(e) => setOperador(e.target.value)}
                  placeholder="vos@deenex.tech"
                  aria-invalid={operadorInvalido}
                  className="w-full mt-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                {operadorInvalido && <p className="text-[11px] text-amber-700 mt-1">Sin espacios, hasta 80 caracteres</p>}
              </div>
            )}

            <button onClick={entrar} disabled={!puedeEntrar}
              className="w-full h-10 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {cargando ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              {elegido === NUEVO ? "Crear y entrar" : "Trabajar sobre esta marca"}
            </button>

            {/* Con sesión de persona no hay «modo operador» del que salir: se sale cerrando sesión. */}
            {esOperador && !persona && (
              <button onClick={salir} className="w-full text-xs text-slate-500 hover:text-red-600 inline-flex items-center justify-center gap-1.5">
                <LogOut size={12} /> Salir del modo operador
              </button>
            )}
          </div>
        )}

        {error && <p className="text-xs text-red-600 mt-3">{error}</p>}
      </div>
    </div>,
    document.body,
  );
}
